export enum GTMEvent {
  COVIDEO_EVENT = 'covideo-event',
  COVIDEO_PAGEVIEW = 'covideo-pageview',
}

export enum GTMPageApplication {
  CDK_VIDEO_WHITELABEL = 'CDK Video Whitelabel',
}

export enum GTMEventName {
  VIDEO_INTERACTION = 'Video Interaction',
  INVENTORY_INTERACTION = 'Inventory Interaction',
  SCHEDULE_MEETING = 'Schedule Meeting',
  SAVED_NEW_VIDEO = 'Saved New Video',
  VIDEO_ATTRIBUTE_INTERACTION = 'Video Attribute Interaction',
  VIDEO_MESSAGE_INTERACTION = 'Video Message Interaction',
  SELECT_RECORDING_TYPE = 'Select Recording Type',
  RECORDING_TOOL_INTERACTION = 'Recording Tool Interaction',
  START_RECORDING = 'Start Recording',
  STOP_RECORDING = 'Stop Recording',
  ADD_VIDEO_ATTRIBUTES = 'Add Video Attributes',
  DISCARD_VIDEO = 'Discard Video',
  RECENTLY_DELETED_INTERACTION = 'Recently Deleted Interaction',
  NEW_LEAD_ADDED = 'New Lead Added',
  CREATE_NEW_MEETING = 'Create New Meeting',
  TELEPROMPTER_SETTINGS_CHANGED = 'Teleprompter Settings Changed',
  DESIGN_SETTINGS_CHANGED = 'Design Settings Changed',
  CUSTOM_ATTRIBUTE_INTERACTION = 'Custom Attribute Interaction',
  EMAIL_SETTINGS_CHANGED = 'Email Settings Changed',
  VIDEO_PLAYER_INTERACTION = 'Video Player Interaction',
  PROFILE_SETTINGS_CHANGED = 'Profile Settings Changed',
  RECORDING_SETTINGS_CHANGED = 'Recording Settings Changed',
  MEETING_ATTRIBUTES_CHANGED = 'Meeting Attributes Changed',
}

export enum PageLocation {
  VIDEOS = 'Videos',
  SEND_AND_SHARE = 'Send & Share',
  MEETINGS = 'Meetings',
  INVENTORY = 'Inventory',
  RECORD = 'Record',
  DETAILS = 'Details',
  MERGE = 'Merge',
}

export enum PageSection {
  VOICE_RECORDER = 'Voice Recorder',
  DETAILS = 'Details',
  EMBED = 'Embed',
  RECORDER = 'Recorder',
  RECORD = 'Record',
  SEND = 'Send',
  SHARE = 'Share',
  SCHEDULE = 'Schedule',
  EDIT_VIDEO = 'Edit Video',
}

// Pageview Attributes
export interface GTMPageviewAttributes {
  /**
   * Event name for GTM or analytics tracking.
   * Always set to `'covideo-pageview'`.
   * @example "covideo-pageview"
   */
  event: GTMEvent.COVIDEO_PAGEVIEW;

  /**
   * Application or product identifier where the pageview originated.
   * Typically fixed to `'CRM Video Embed'`.
   * @example "'CRM Video Embed'"
   */
  application: GTMPageApplication.CDK_VIDEO_WHITELABEL;

  /**
   * Full URL of the page viewed, including protocol, domain, and path.
   * @example "https://video.connectcdk.com/reports/companies"
   */
  page_url: string;

  /**
   * Higher-level section or context of the page being viewed
   * (e.g., "Reports", "Library", "Dashboard").
   * @example "Reports"
   */
  page_location?: string;
  /**
   * **Lower-level location within the selected `page_section`.**
   * Represents a specific page or sub-route under that section.
   * @example "My Team"
   */
  page_section?: string;
}

// Event Attributes
export interface GTMEventAttributes {
  /**
   * Event name for GTM or analytics tracking.
   * Always set to `'covideo-name'`.
   * @example "covideo-name"
   */
  event: GTMEvent.COVIDEO_EVENT;

  // problably missing video message interaction
  /**
   * Specific name of the event being tracked.
   * Examples include 'Video Interaction', 'Inventory Interaction', 'Meeting Interaction', 'Schedule Meeting', 'Save New Video', and 'Video Attribute Interaction'.
   * @example "Video Interaction"
   */
  event_name: GTMEventName;
}

// CDK User Attributes
export interface GTMCDKUserAttributes {
  /**
   * Unique identifier of the user within our system.
   * @example "2343564"
   */
  user_id?: string;

  /**
   * One or more roles assigned to the user.
   * @example ["Sales Person", "F&I Manager"]
   */
  user_role?: string[];

  /**
   * Enterprise identifier (CDK enterprise code).
   * @example "E000118"
   */
  enterprise_id?: string;

  /**
   * Enterprise (dealer group) display name.
   * @example "Excellence Motors"
   */
  enterprise_name?: string;

  /**
   * Store identifier (CDK store code).
   * @example "S13552"
   */
  store_id?: string;

  /**
   * Store display name.
   * @example "Toyota Boston"
   */
  store_name?: string;

  /**
   * Customer identifier from the CRM (if present).
   * @example "123454"
   */
  customer_id?: string;

  /**
   * Flag indicating whether the user is an internal user (CDK/Covideo staff).
   * @example true
   */
  is_internal_user?: boolean;
}

// Video Attributes
export interface GTMVideoAttributes {
  /**
   * The ID of the video that is selected, created, or currently present on the page.
   * @example 987654
   */
  video_id?: number;

  /**
   * The category/type associated with the video.
   * Common examples: "Marketing", "Prospecting", "Respond to Inquiry", "Thanks for Your Business".
   * @example "Marketing"
   */
  video_type?: string | undefined;

  /**
   * Whether the video has the customer's name filled in.
   * NOTE: By cdk documentation this is a boolean check if that is correct.
   * @example true
   */
  video_customer_name?: boolean;

  /**
   * Length of the video in **minutes** as a decimal value.
   * For example, "00:02:45" (2 minutes 45 seconds) -> 2.75 minutes.
   * @example 2.75
   */
  video_length?: number;

  /**
   * Title of the video.
   * NOTE: By cdk documentation this is The inventory ID of the vehicle related to this video (as provided for tracking).
   * @example 123456
   */
  video_title?: string;
}

// Vehicle Attributes
export interface GTMVehicleAttributes {
  /**
   * Vehicle description.
   * @example "2019 Ford Taurus"
   */
  vehicle?: string;

  /**
   * Vehicle price in numeric form (no currency symbol).
   * @example 17499
   */
  price?: number;

  /**
   * Vehicle Identification Number (VIN).
   * @example "1FAHP2E89KG117472"
   */
  vin?: string;

  /**
   * Dealer stock number.
   * @example "AU117472"
   */
  stock_number?: string;

  /**
   * Vehicle condition (e.g., New, Used, CPO).
   * @example "Used"
   */
  condition?: string;

  /**
   * Engine details or code.
   * @example "3.5L Ti-VCT V6 (FFV)"
   */
  engine?: string;

  /**
   * Manufacturer / make.
   * @example "Ford"
   */
  make?: string;

  /**
   * Vehicle model.
   * @example "Taurus"
   */
  model?: string;

  /**
   * Trim and/or body style.
   * @example "SEL 4dr Front-wheel Drive Sedan"
   */
  trim?: string;

  /**
   * Model year.
   * @example 2019
   */
  year?: number;

  /**
   * Odometer reading in miles.
   * @example 39231
   */
  mileage?: number;

  /**
   * Exterior color.
   * @example "Red"
   */
  ext_color?: string;

  /**
   * Interior color.
   * @example "Dune"
   */
  int_color?: string;

  /**
   * Transmission type.
   * @example "Automatic"
   */
  transmission?: string;

  /**
   * Vehicle type.
   * @example "Sedan"
   */
  vehicle_type?: string;
}

// covideo mapper video and vehicle attributes
export interface CovideoGTMVideoAttributes {
  videoId?: number;
  videoType?: string;
  videoCustomerName?: boolean;
  videoLength?: number;
  videoTitle?: string;
}

export interface CovideoGTMVehicleItem {
  title: string;
  price: string | number;
  year: string | number;
  mileage: string | number;
  vin: string;
  stockNumber: string | number;
  itemCondition: string;
  engine: string;
  make: string;
  model: string;
  trim: string;
  exteriorColor: string;
  interiorColor: string;
  transmission: string;
  vehicleType: string;
}
